export type PaymentStatus =
  | "pending"
  | "processing"
  | "succeeded"
  | "failed"
  | "cancelled";

export interface PaymentAttempt {
  id: string;
  tenant_id: string;
  session_id: string;
  idempotency_key: string;
  status: PaymentStatus;
  amount: number;
  currency: string;
  provider_ref?: string;
  failure_reason?: string;
  metadata?: string;
  created_at: string;
  updated_at: string;
}

export interface PaymentListResponse {
  data: PaymentAttempt[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

/** Escopo de tentativas que pertencem a uma ordem OMS. */
export interface PaymentScope {
  orderId: string;
  /** checkout_session_id da ordem (vazio em ordens OMS-internas/quote). */
  session?: string;
  /** payment_attempt_id registado na ordem (CAS, single-registration). */
  attemptId?: string;
}

/**
 * Extrai metadata.order_id de uma tentativa de pagamento (coercing numérico).
 * A API de listagem é tenant-wide — o escopo à ordem é feito no cliente.
 */
export function parsePaymentOrderId(raw?: string): string | undefined {
  if (!raw) return undefined;
  try {
    const v = (JSON.parse(raw) as Record<string, unknown> | null)?.order_id;
    return v == null || v === "" ? undefined : String(v);
  } catch {
    return undefined;
  }
}

/**
 * Decide se uma tentativa pertence à ordem:
 * - metadata.order_id presente → comparação directa (exclui outras ordens);
 * - sem metadata → session é a regra (checkout sessions são 1:1 com a ordem;
 *   ordens OMS-internas registam tentativas com session_id == order.id);
 * - payment_attempt_id é fallback final (legado CAS single-registered).
 */
export function isAttemptForOrder(
  attempt: PaymentAttempt,
  scope: PaymentScope,
): boolean {
  const metaOrder = parsePaymentOrderId(attempt.metadata);
  if (metaOrder) return metaOrder === scope.orderId;
  return (
    attempt.session_id === scope.orderId ||
    (!!scope.session && attempt.session_id === scope.session) ||
    attempt.id === scope.attemptId
  );
}
